import React, { useEffect, useState } from "react";
import { useQuiz } from "../../../../context/QuizContext";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import axios from "axios";
import "./psychometric.css";

function Questions() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const { responses, handleOptionChange } = useQuiz();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true); // for loading spinner
  const [error, setError] = useState(null); // for error handling

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async () => {
    if (responses.length === questions.length) {
      setIsSubmitting(true);
      navigate("/psychometric/submit");
      setIsSubmitting(false);
    } else {
      toast.error("Please answer all questions.");
    }
  };

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/swot/questions`
      );
      setQuestions(response.data.questions_list);
    } catch (error) {
      setError("Failed to load questions. Please try again later.");
      toast.error("Error fetching questions.");
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion =
    questions.length > 0 ? questions[currentQuestionIndex] : null;

  const selectedOption = currentQuestion
    ? responses.find((response) => response.question_id === currentQuestion.id)
        ?.selected_option
    : null;

  useEffect(() => {
    fetchQuestions();
  }, []);

  if (loading) {
    return (
      <div className="psych-page">
        <div className="psych-loader">
          <div className="psych-spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="psych-page">
        <div className="psych-loader">
          <p style={{ color: "#f87171" }}>{error}</p>
        </div>
      </div>
    );
  }

  const progress = questions.length > 0
    ? ((currentQuestionIndex + 1) / questions.length) * 100
    : 0;

  return (
    currentQuestion && (
      <div className="psych-page">
        <div className="psych-particles">
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
        </div>

        <div className="psych-questions">
          {/* Header with AI badge and counters */}
          <div className="psych-q-header">
            <div className="ai-badge" style={{ fontSize: "0.7rem", padding: "4px 12px" }}>
              <span className="ai-dot"></span>
              AI Assessment
            </div>
            <div style={{ display: "flex", gap: "1.5rem", alignItems: "center" }}>
              <span className="q-counter">
                Question <span>{currentQuestionIndex + 1}</span> of <span>{questions.length}</span>
              </span>
              <span className="q-counter">
                Answered: <span>{responses.length}</span> / <span>{questions.length}</span>
              </span>
            </div>
          </div>

          {/* Progress bar */}
          <div className="psych-progress-track">
            <div className="psych-progress-fill" style={{ width: `${progress}%` }}></div>
          </div>

          {/* Question card */}
          <div className="psych-q-card">
            <div className="q-number">Question {currentQuestionIndex + 1}</div>
            <div className="q-text">{currentQuestion.question_text}</div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
              <label className={`psych-option ${selectedOption === "yes" ? "selected" : ""}`}>
                <input
                  type="radio"
                  name={`question-${currentQuestion.id}`}
                  value="yes"
                  checked={selectedOption === "yes"}
                  onChange={() => handleOptionChange(currentQuestion.id, "yes")}
                />
                <span>{currentQuestion.option_a}</span>
              </label>

              <label className={`psych-option ${selectedOption === "no" ? "selected" : ""}`}>
                <input
                  type="radio"
                  name={`question-${currentQuestion.id}`}
                  value="no"
                  checked={selectedOption === "no"}
                  onChange={() => handleOptionChange(currentQuestion.id, "no")}
                />
                <span>{currentQuestion.option_b}</span>
              </label>
            </div>
          </div>

          {/* Navigation */}
          <div className="psych-q-nav">
            <button
              onClick={handleBack}
              disabled={currentQuestionIndex === 0}
              className="btn-back"
            >
              ← Back
            </button>
            {currentQuestionIndex < questions.length - 1 ? (
              <button onClick={handleNext} className="btn-next">
                Next →
              </button>
            ) : isSubmitting ? (
              <button className="btn-next" disabled style={{ opacity: 0.7 }}>
                <div className="psych-spinner" style={{ width: 18, height: 18, borderWidth: 2 }}></div>
                Submitting...
              </button>
            ) : (
              <button onClick={handleSubmit} className="btn-next">
                Submit ✓
              </button>
            )}
          </div>
        </div>
      </div>
    )
  );
}

export default Questions;
